<template>
  <div v-show="active" class="tab-ciudades p-4">
    <!-- Encabezado con botones de acción -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h5 class="section-title text-white mb-0" v-if="!userHasEnrolledOffer">Selecciona una ciudad para inscribirte</h5>
      <div class="d-flex gap-2">
        <button
          v-if="userStatus.canPay"
          class="btn btn-success"
          @click.prevent="$emit('pay')"
        >
          <span class="btn-text-label">PAGAR</span>
        </button>
        <button
          v-if="!userStatus.canPay"
          class="btn btn-golden-hover"
          :disabled="!selection.selectedOfferId || (userStatus.alreadyRegistered && !userStatus.isRejected)"
          @click.prevent="$emit('postulate')"
        >
          <span class="btn-text-label">
            {{ userStatus.isRejected ? 'VOLVER A POSTULARME' : (userStatus.alreadyRegistered ? 'YA ESTÁS INSCRITO' : 'INSCRIBIRME') }}
          </span>
          <span class="btn-icon-circle">
            <iconify-icon icon="lucide:arrow-up-right" class="icon-initial" />
            <iconify-icon icon="lucide:arrow-right" class="icon-hover" />
          </span>
        </button>
      </div>
    </div>

    <!-- Filtro por estado -->
    <div class="row mb-4">
      <div class="col-12 col-md-6 col-lg-4">
        <label class="form-label text-white mb-1">Filtrar por estado</label>
        <multiselect
          :model-value="selection.selectedState"
          :options="data.states"
          label="name"
          track-by="id"
          placeholder="Todos los estados"
          :allow-empty="false"
          selected-label=""
          select-label=""
          deselect-label=""
          @update:model-value="$emit('update:selected-state', $event)"
        />
      </div>
    </div>

    <!-- Estado de carga -->
    <div v-if="loading" class="d-flex justify-content-center py-5">
      <div class="loader-icon mx-auto"></div>
    </div>

    <!-- Sin resultados -->
    <div v-else-if="data.offers.length === 0" class="text-center text-white py-5">
      <iconify-icon icon="lucide:map-off" style="font-size: 48px; opacity: 0.5;" />
      <p class="mt-3 opacity-75">No hay ciudades disponibles para este curso.</p>
    </div>

    <!-- Grid de ofertas -->
    <div v-else class="cities-grid">
      <PlatformOfferCard
        v-for="offer in data.offers"
        :key="offer.id"
        :offer="offer"
        :selected="selection.selectedOfferId === offer.id || offer.isUserEnrolled"
        :disabled="userHasEnrolledOffer && !offer.isUserEnrolled"
        @select="$emit('select-offer', $event)"
        @show-breakdown="$emit('show-breakdown', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Multiselect from 'vue-multiselect';
import PlatformOfferCard from './PlatformOfferCard.vue';

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({ offers: [], states: [] }),
  },
  selection: {
    type: Object,
    default: () => ({ selectedOfferId: null, selectedState: null }),
  },
  userStatus: {
    type: Object,
    default: () => ({ canPay: false, alreadyRegistered: false, isRejected: false }),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['update:selected-state', 'select-offer', 'postulate', 'pay', 'show-breakdown']);

const userHasEnrolledOffer = computed(() => props.data.offers.some((o) => o.isUserEnrolled));
</script>

<style scoped>
.tab-ciudades {
  min-height: 400px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.cities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.loader-icon {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6d263a;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn-golden-hover {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #b49b57;
  border: none;
  border-radius: 50px;
  padding: 6px;
  font-weight: 600;
  color: #1a1a1a;
  overflow: hidden;
  min-width: 180px;
  height: 48px;
  transition: background-color 0.3s ease;
}

.btn-golden-hover:hover:not(:disabled) {
  background-color: #d1b25d;
}

.btn-golden-hover:disabled {
  background-color: #b7b7b7;
  color: #666;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-golden-hover .btn-icon-circle {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #fff;
  color: #1a1a1a;
  border-radius: 50%;
  font-size: 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  right: 6px;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    right 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    left 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-golden-hover:hover:not(:disabled) .btn-icon-circle {
  right: auto;
  left: 6px;
}

.btn-golden-hover .btn-text-label {
  white-space: nowrap;
  color: #1a1a1a;
  margin-left: 12px;
  margin-right: 48px;
  transition: margin 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-golden-hover:hover:not(:disabled) .btn-text-label {
  margin-left: 48px;
  margin-right: 12px;
}

.btn-golden-hover .icon-initial,
.btn-golden-hover .icon-hover {
  position: absolute;
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.btn-golden-hover .icon-initial {
  opacity: 1;
  transform: rotate(0deg);
}

.btn-golden-hover .icon-hover {
  opacity: 0;
  transform: rotate(-90deg);
}

.btn-golden-hover:hover:not(:disabled) .icon-initial {
  opacity: 0;
  transform: rotate(90deg);
}

.btn-golden-hover:hover:not(:disabled) .icon-hover {
  opacity: 1;
  transform: rotate(0deg);
}

.btn-golden-hover:disabled .btn-icon-circle {
  background-color: #e0e0e0;
  color: #999;
}

@media (max-width: 768px) {
  .cities-grid {
    grid-template-columns: 1fr;
  }
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
